// src/api/contactCompanyApi.ts
//
// GATE B5 — the frontend data layer for Contact <-> Company affiliations.
//
// ONE relationship, read from two directions. There is deliberately no separate
// client-side state for Contact->Company vs Company->Contact: both endpoints
// return the same canonical `contact_companies` rows created in B1-B4, and both
// mutation paths call the same backend service.
//
// CAPABILITY / ROUTE PAIRING — the component gates on the capability that
// matches the ROUTE it actually calls:
//
//   contact-side routes  (/api/contacts/:id/companies)   contacts.read / contacts.write
//   company-side routes  (/api/companies/:id/contacts)   companies.read / companies.write
//
// A component must never call a contact-side route to dodge a company-side
// capability check — the backend would still enforce the company permission and
// the UI would be lying about what it is doing.

import { baseApi } from './baseApi';

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * How a person relates to a company. Mirrors the server's
 * `CONTACT_COMPANY_RELATIONSHIP_TYPES`. NOT the Contact's own
 * `relationshipType` (the personal-workspace spouse/doctor/... field).
 */
export const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'board_member', label: 'Board member' },
  { value: 'investor', label: 'Investor' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
] as const;
export type AffiliationRelationshipType = (typeof RELATIONSHIP_TYPE_OPTIONS)[number]['value'];

export function relationshipTypeLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return RELATIONSHIP_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/** Descriptive fields every affiliation carries, from either direction. */
export interface AffiliationRelationshipFields {
  roleTitle: string | null;
  isPrimary: boolean;
  relationshipType: AffiliationRelationshipType | null;
  /** `YYYY-MM-DD`; null = unknown. */
  startDate: string | null;
  /** `YYYY-MM-DD`; null = ongoing. */
  endDate: string | null;
  /** Derived by the server from `endDate` — never stored. */
  isCurrent: boolean;
}

/** Optional details for one link made by a create-with-links form. */
export interface CreateLinkDetails {
  roleTitle?: string | null;
  relationshipType?: AffiliationRelationshipType | null;
  startDate?: string | null;
}

export interface AffiliationCompany {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
}

export interface AffiliationContact {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  type: string | null;
}

/** An affiliation as returned by the contact-side routes. */
export interface ContactCompanyAffiliation extends AffiliationRelationshipFields {
  affiliationId: string;
  companyId: string;
  company: AffiliationCompany | null;
}

/** The same row as returned by the company-side routes. */
export interface CompanyContactAffiliation extends AffiliationRelationshipFields {
  affiliationId: string;
  contactId: string;
  contact: AffiliationContact | null;
}

export interface LinkCompanyToContactArgs extends CreateLinkDetails {
  contactId: string;
  companyId: string;
  isPrimary?: boolean;
}

export interface LinkContactToCompanyArgs extends CreateLinkDetails {
  companyId: string;
  contactId: string;
  isPrimary?: boolean;
}

export interface UpdateAffiliationArgs {
  /** Which surface the user is on — decides the route, and therefore the permission. */
  side: 'contact' | 'company';
  /** Owning entity id for the route (contactId for 'contact', companyId for 'company'). */
  ownerId: string;
  affiliationId: string;
  /** The counterpart id, needed only for cache invalidation. */
  counterpartId: string;
  roleTitle?: string | null;
  isPrimary?: boolean;
  relationshipType?: AffiliationRelationshipType | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Only used when this edit ENDS the primary: the current affiliation to promote. */
  replacementPrimaryId?: string;
  /** Only used when this edit ends the primary: explicitly accept "no primary". */
  allowNoPrimary?: boolean;
}

export interface RemoveAffiliationArgs {
  side: 'contact' | 'company';
  ownerId: string;
  affiliationId: string;
  counterpartId: string;
  /** Promote this affiliation when removing the current primary. */
  replacementPrimaryId?: string;
  /** Explicitly accept leaving the contact with no primary company. */
  allowNoPrimary?: boolean;
}

/**
 * Invalidate BOTH sides of the relationship plus the caches that display a
 * derived primary company.
 *
 * An affiliation change can move the primary, which rewrites
 * `contacts.companyId` / `contacts.company`. Those feed the contacts list
 * column and the contact summary, so a contact-detail-only invalidation would
 * leave a stale company name on screen. The company list is invalidated because
 * the affiliation-aware contact COUNT changes.
 *
 * Deliberately targeted: specific ids plus the two LIST tags, never a
 * `baseApi.util.resetApiState()` sledgehammer.
 */
function affiliationTags(contactId: string, companyId: string) {
  return [
    { type: 'Contact' as const, id: contactId },
    { type: 'Contact' as const, id: `context-${contactId}` },
    { type: 'Contact' as const, id: 'LIST' as const },
    { type: 'Company' as const, id: companyId },
    { type: 'Company' as const, id: 'LIST' as const },
  ];
}

export const contactCompanyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Contact side ───────────────────────────────────────────────────────
    getContactCompanies: builder.query<ContactCompanyAffiliation[], string>({
      query: (contactId) => `/contacts/${contactId}/companies`,
      providesTags: (_r, _e, contactId) => [
        { type: 'Contact', id: `affiliations-${contactId}` },
      ],
    }),

    linkCompanyToContact: builder.mutation<ContactCompanyAffiliation, LinkCompanyToContactArgs>({
      query: ({ contactId, ...body }) => ({
        url: `/contacts/${contactId}/companies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { contactId, companyId }) => [
        { type: 'Contact', id: `affiliations-${contactId}` },
        { type: 'Company', id: `affiliations-${companyId}` },
        ...affiliationTags(contactId, companyId),
      ],
    }),

    // ── Company side ───────────────────────────────────────────────────────
    getCompanyContacts: builder.query<CompanyContactAffiliation[], string>({
      query: (companyId) => `/companies/${companyId}/contacts`,
      providesTags: (_r, _e, companyId) => [
        { type: 'Company', id: `affiliations-${companyId}` },
      ],
    }),

    linkContactToCompany: builder.mutation<CompanyContactAffiliation, LinkContactToCompanyArgs>({
      query: ({ companyId, ...body }) => ({
        url: `/companies/${companyId}/contacts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { contactId, companyId }) => [
        { type: 'Contact', id: `affiliations-${contactId}` },
        { type: 'Company', id: `affiliations-${companyId}` },
        ...affiliationTags(contactId, companyId),
      ],
    }),

    // ── Shared mutations, routed by surface ────────────────────────────────
    //
    // One endpoint each rather than four: the row is the same, only the URL —
    // and therefore the permission the backend enforces — differs.
    updateAffiliation: builder.mutation<ContactCompanyAffiliation, UpdateAffiliationArgs>({
      query: ({ side, ownerId, affiliationId, counterpartId: _counterpart, ...body }) => ({
        url:
          side === 'contact'
            ? `/contacts/${ownerId}/companies/${affiliationId}`
            : `/companies/${ownerId}/contacts/${affiliationId}`,
        method: 'PATCH',
        // Undefined keys are dropped by JSON serialization, so an omitted
        // field means "leave as is" on the server.
        body,
      }),
      invalidatesTags: (_r, _e, { side, ownerId, counterpartId }) => {
        const contactId = side === 'contact' ? ownerId : counterpartId;
        const companyId = side === 'contact' ? counterpartId : ownerId;
        return [
          { type: 'Contact' as const, id: `affiliations-${contactId}` },
          { type: 'Company' as const, id: `affiliations-${companyId}` },
          ...affiliationTags(contactId, companyId),
        ];
      },
    }),

    removeAffiliation: builder.mutation<void, RemoveAffiliationArgs>({
      query: ({ side, ownerId, affiliationId, replacementPrimaryId, allowNoPrimary }) => ({
        url:
          side === 'contact'
            ? `/contacts/${ownerId}/companies/${affiliationId}`
            : `/companies/${ownerId}/contacts/${affiliationId}`,
        method: 'DELETE',
        // Sent in the body so the decision is explicit and auditable rather
        // than a query-string flag. The backend accepts either.
        body: { replacementPrimaryId, allowNoPrimary },
      }),
      invalidatesTags: (_r, _e, { side, ownerId, counterpartId }) => {
        const contactId = side === 'contact' ? ownerId : counterpartId;
        const companyId = side === 'contact' ? counterpartId : ownerId;
        return [
          { type: 'Contact' as const, id: `affiliations-${contactId}` },
          { type: 'Company' as const, id: `affiliations-${companyId}` },
          ...affiliationTags(contactId, companyId),
        ];
      },
    }),
  }),
});

export const {
  useGetContactCompaniesQuery,
  useLinkCompanyToContactMutation,
  useGetCompanyContactsQuery,
  useLinkContactToCompanyMutation,
  useUpdateAffiliationMutation,
  useRemoveAffiliationMutation,
} = contactCompanyApi;

/**
 * Turn an affiliation API failure into something a person can act on.
 *
 * Deliberately does NOT surface the server's message verbatim: the backend
 * returns the same NOT_FOUND for a missing affiliation and for one belonging to
 * another workspace, precisely so the API never confirms a foreign tenant's
 * data. Echoing raw text would also leak Sequelize/driver detail into the UI.
 */
export function affiliationErrorMessage(err: unknown): string {
  const code = (err as { data?: { code?: string } })?.data?.code;
  const status = (err as { status?: number })?.status;

  switch (code) {
    case 'PRIMARY_REPLACEMENT_REQUIRED':
      return 'Choose which company becomes primary before removing this one.';
    case 'PRIMARY_MUST_BE_CURRENT':
      return 'The primary company must be a current relationship. Choose another current company as primary, or leave no primary.';
    case 'INVALID_RELATIONSHIP':
      return 'Some relationship details are invalid. Check the highlighted fields.';
    case 'CROSS_WORKSPACE':
      return 'That record is not available in this workspace.';
    case 'CONTACT_NOT_FOUND':
    case 'COMPANY_NOT_FOUND':
    case 'AFFILIATION_NOT_FOUND':
      return 'That record could no longer be found. Refresh and try again.';
    default:
      break;
  }
  if (status === 403) return 'You do not have permission to change company links.';
  if (status === 409) return 'That change conflicts with the current company links.';
  return 'Something went wrong updating company links. Please try again.';
}

/**
 * Field-specific validation messages from a 400 INVALID_RELATIONSHIP, keyed by
 * field (`roleTitle`, `relationshipType`, `startDate`, `endDate`) — or, for a
 * create-with-links request, by `<linkedId>.<field>`. Empty for any other error.
 * These are the server's own validation sentences, never driver text.
 */
export function relationshipFieldErrors(err: unknown): Record<string, string> {
  const data = (err as { data?: { code?: string; fields?: unknown } })?.data;
  if (data?.code !== 'INVALID_RELATIONSHIP' || !data.fields || typeof data.fields !== 'object') {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(data.fields as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}
